'use client';

import { Fragment, useCallback } from 'react';

import { MainWalletBase } from '@cosmos-kit/core';
import { Trans } from 'react-i18next';

import { Modal } from 'app/components/core/Modal/Modal';
import { Tooltip } from 'app/components/core/Tooltip/Tooltip';
import { Button } from 'app/shared/buttons/Button';
import { Icons } from 'app/constants/icons';
import { COSMOS_KIT_WALLETS_INFO } from 'app/constants/wallets';
import {
  useAuthentication,
  useCosmosKitManager,
} from 'app/hooks/useAuthentication';
import { CosmosKitWalletType } from 'app/types/providers';
import { ModalHeader } from 'app/shared/misc/modals/ModalHeader/ModalHeader';

export const WalletsModal = () => {
  const {connect} = useAuthentication();
  const manager = useCosmosKitManager();

  const handleProviderClick = useCallback(
    (walletType: CosmosKitWalletType) => {
      connect('cosmos_kit', false, {
        manager,
        walletType,
      });
    },
    [connect, manager],
  );

  return (
    <Modal
      className="flex w-full max-w-[450px] flex-col gap-5 px-8.75 py-[30px] mobile:px-5"
      id="connect-wallets"
      hideCloseButton>
      <ModalHeader>
        <span>
          <Trans i18nKey="connect.connectWith" />
        </span>

        <Tooltip id="wallets-modal-tip">
          <Trans i18nKey="connect.connectWithWalletTooltip" />
        </Tooltip>

        <Button
          data-tooltip-target="wallets-modal-tip"
          className="flex cursor-default items-center p-[5px]">
          <Icons.question className="self-top h-4.5 w-4.5 text-neutral-400" />
        </Button>
      </ModalHeader>

      <div className="flex w-full flex-col items-center justify-between gap-[5px] desktop:min-w-full">
        {Object.entries(COSMOS_KIT_WALLETS_INFO).map(
          ([provider, {isInstalled, isMobile: isMobileProvider}]) => {
            const chainWallet = manager.mainWallets.find(
              e => e.walletName === provider,
            );

            if (
              !chainWallet ||
              (isMobileProvider && !isInstalled?.(chainWallet))
            ) {
              return <Fragment key={provider} />;
            }

            return (
              <WalletProviderButton
                key={provider}
                chainWallet={chainWallet}
                walletType={provider as CosmosKitWalletType}
                onClick={handleProviderClick}
              />
            );
          },
        )}
      </div>
    </Modal>
  );
};

type WalletProviderButtonProps = {
  onClick: (provider: CosmosKitWalletType) => void;
  walletType: CosmosKitWalletType;
  chainWallet: MainWalletBase;
};

const WalletProviderButton = ({
  walletType,
  onClick,
  chainWallet,
}: WalletProviderButtonProps) => {
  const {name, Icon, isDisabled, isMobile, isInstalled} =
    COSMOS_KIT_WALLETS_INFO[walletType];

  const installed = isInstalled?.(chainWallet);

  const handleClick = useCallback(async () => {
    if (!isMobile && !installed) {
      if (chainWallet.downloadInfo) {
        window.open(chainWallet.downloadInfo.link, '_blank');
      }

      return;
    }

    return onClick(walletType);
  }, [onClick, walletType, chainWallet, installed, isMobile]);

  //console.log('qrState', qrState, qrMsg, directChainWallet.qrUrl);

  if (isDisabled) return null;

  return (
    <Button
      // {...buildEvent('button_login', walletType)}
      variant="secondary-black-contained"
      className="group h-[90px] w-full items-center gap-4 !px-4 mobile:h-[65px]"
      size="xl"
      onClick={handleClick}
      aria-label={name}>
      {Icon && <Icon className="h-10 w-10 mobile:h-6 mobile:w-6" />}

      <div className="flex flex-1 flex-col text-left">
        <span className="justify-content flex items-center gap-[5px] text-base text-white group-hover:text-white light:text-neutral-800">
          <span>{name}</span>
          {isMobile && <WalletMobileTag />}
        </span>

        {!isMobile && !installed && (
          <span className="text-[13px] font-light text-neutral-100 group-hover:text-neutral-100 light:text-neutral-400 light:group-hover:text-neutral-80">
            Not installed yet
          </span>
        )}
      </div>

      <Icons.chevronRight
        className="-mr-1.5 text-neutral-400 group-hover:text-neutral-80 light:text-neutral-500"
        size={19}
      />
    </Button>
  );
};

export const WalletMobileTag = () => {
  return (
    <span className="rounded bg-charcoal-400 px-1 py-px text-[8px] uppercase text-white">
      Mobile
    </span>
  );
};
